import {
  HyamCookiePreferencesLink,
  isCookiePreferencesItem,
} from "hyam-core/components/HyamIubenda";
import _ from "lodash";
import {
  FooterDocument,
  FooterColumnLinksWithTitleSlice,
  FooterColumnRichTextSlice,
  FooterColumnActionItemsSlice,
  FooterColumnRichTextSliceDefaultPrimary,
} from "prismicio-types";
import { LinkField } from "@prismicio/types";
import { withConfigure } from "hyam-core/utils/withConfigure";
import { styles as moduleStyles, HyamPrismicDocumentFooterMobileStyles } from "./styles";
import {
  HyamPrismicLink,
  HyamPrismicRichText,
  HyamPrismicButton,
  HyamPrismicImage,
  HyamPrismicAccordion,
} from "hyam-prismic/components/elements";
import PlusWhite from "svg/plus-16px_white.svg";
import MinusWhite from "svg/minus-16px_white.svg";
import ArrowWhite from "svg/arrow-right-24px_white.svg";

export type HyamPrismicDocumentFooterMobileProps = {
  document: FooterDocument;
  styles?: Partial<HyamPrismicDocumentFooterMobileStyles>;
  stylesReplace?: Partial<HyamPrismicDocumentFooterMobileStyles>;
};

type SectionLinksProps = {
  styles: HyamPrismicDocumentFooterMobileStyles;
  title?: string;
  items: {
    link_label: string | null;
    link: LinkField;
  }[];
};

const SectionLinks = (props: SectionLinksProps) => {
  const { styles, items } = props;

  return (
    <div className={styles.section_links}>
      {_.map(items, (item, key) => {
        return (
          <HyamPrismicLink
            key={key}
            field={item.link}
            className={styles.section_links_link}
          >
            {item.link_label}
          </HyamPrismicLink>
        );
      })}
    </div>
  );
};

type SectionRichTextProps = {
  styles: HyamPrismicDocumentFooterMobileStyles;
  title?: string;
  items: {
    primary: FooterColumnRichTextSliceDefaultPrimary;
  }[];
};

const SectionRichText = (props: SectionRichTextProps) => {
  const { styles, items } = props;

  return (
    <div className={styles.section_rich_text}>
      {_.map(items, (item, key) => {
        return (
          <div className={styles.section_rich_text_item} key={key}>
            <HyamPrismicRichText
              key={key}
              field={item.primary.content}
              styles={{
                all: styles.rich_text_all,
                paragraph: styles.rich_text_paragraph,
              }}
              stylesReplace={{
                hyperlink: styles.rich_text_hyperlink,
              }}
            />
            <HyamPrismicButton
              field={item.primary.button_link}
              styles={{ container: styles.section_rich_text_button }}
              rightIcon={<ArrowWhite />}
            >
              {item.primary.button_label}
            </HyamPrismicButton>
          </div>
        );
      })}
    </div>
  );
};

type SectionActionItemsProps = {
  styles: HyamPrismicDocumentFooterMobileStyles;
  slice: FooterColumnActionItemsSlice;
};

const SectionActionItems = (props: SectionActionItemsProps) => {
  const {
    styles,
    slice: { primary, items },
  } = props;

  return (
    <div className={styles.section_action_items}>
      <HyamPrismicButton
        variant={"Contained Secondary Button"}
        field={primary.item_1_link}
        styles={{
          prismic_link_wrapper: styles.section_action_button,
          container_contained_secondary: styles.section_action_button,
        }}
      >
        {primary.item_1_link_label_mobile}
      </HyamPrismicButton>
      <HyamPrismicButton
        variant={"Contained Secondary Button"}
        field={primary.item_2_link}
        styles={{
          prismic_link_wrapper: styles.section_action_button,
          container_contained_secondary: styles.section_action_button,
        }}
      >
        {primary.item_2_link_label_mobile}
      </HyamPrismicButton>
      <div className={styles.section_action_items_social}>
        {_.map(items, (item, key) => {
          return (
            <HyamPrismicLink
              key={key}
              field={item.social_media_link}
              className={styles.section_action_item_social_link}
            >
              <img
                src={item.social_media_icon.url || ""}
                alt={item.social_media_icon.alt || ""}
              />
              ;
            </HyamPrismicLink>
          );
        })}
      </div>
    </div>
  );
};

type SectionLegalProps = {
  styles: HyamPrismicDocumentFooterMobileStyles;
  title: string;
  items: {
    link_label: string | null;
    link: LinkField;
  }[];
};

const SectionLegal = (props: SectionLegalProps) => {
  const { title, items, styles } = props;

  console.log("___ SectionLegal", items);

  return (
    <div>
      <div className={styles.item_title}>{title}</div>
      <div className={styles.legal_links_container}>
        {_.map(items, (item, key) =>
          // "Cookie Policy" opens the Iubenda preferences popup
          isCookiePreferencesItem(item) ? (
            <HyamCookiePreferencesLink key={key} className={styles.legal_link}>
              {item.link_label}
            </HyamCookiePreferencesLink>
          ) : (
            <HyamPrismicLink key={key} field={item.link} className={styles.legal_link}>
              {item.link_label}
            </HyamPrismicLink>
          ),
        )}
      </div>
    </div>
  );
};

const _HyamPrismicDocumentFooterMobile = (
  props: HyamPrismicDocumentFooterMobileProps,
) => {
  const styles = props.styles as HyamPrismicDocumentFooterMobileStyles;
  const document = props.document;

  if (!document?.data) return <div>HyamPrismicDocumentFooter: Missing data</div>;

  const slices1 = document.data.slices1 as FooterColumnLinksWithTitleSlice[];
  const slices2 = document.data.slices2;
  const slices3 = document.data.slices3;

  const slices2_0 = slices2?.[0] as FooterColumnLinksWithTitleSlice;
  const slices2_12 = [slices2?.[1], slices2?.[2]] as FooterColumnRichTextSlice[];
  const slices2_3 = slices2?.[3] as FooterColumnActionItemsSlice;
  const slices3_0 = slices3?.[0] as FooterColumnLinksWithTitleSlice;

  const treatments: SectionLinksProps = {
    styles,
    title: slices1?.[0]?.primary?.title || "",
    items: _.flatten(
      _.map(slices1, (slice) =>
        _.map(slice.items, ({ link_label = "", link }) => ({ link_label, link })),
      ),
    ),
  };

  const navigation: SectionLinksProps = {
    styles,
    title: slices2_0.primary?.title || "",
    items: _.flatten(
      _.map(slices2_0.items, ({ link_label = "", link }) => ({ link_label, link })),
    ),
  };

  const locations: SectionRichTextProps = {
    styles,
    title:
      document.data.locations_title_mobile || "Locations Title Mobile Missing in Prismic",
    items: _.flatten(_.map(slices2_12, ({ primary }) => ({ primary }))),
  };

  const actionItems = {
    styles,
    slice: slices2_3,
  };

  const legal = {
    styles,
    title: slices3_0.primary?.title || "",
    items: _.flatten(
      _.map(slices3, (slice) =>
        _.map(slice.items, ({ link_label = "", link }) => ({ link_label, link })),
      ),
    ),
  };

  return (
    <div id="footermobile" className={styles.layout_container}>
      <div className={styles.container}>
        <HyamPrismicAccordion
          openIcon={<PlusWhite />}
          closeIcon={<MinusWhite />}
          styles={{
            container: styles.accordion_container,
            item_title: styles.accordion_item_title,
          }}
          items={[
            {
              title: <div className={styles.item_title}>{treatments.title}</div>,
              content: <SectionLinks {...treatments} />,
            },
            {
              title: <div className={styles.item_title}>{navigation.title}</div>,
              content: <SectionLinks {...navigation} />,
            },
            {
              title: <div className={styles.item_title}>{locations.title}</div>,
              content: <SectionRichText {...locations} />,
            },
          ]}
        />
        <SectionActionItems {...actionItems} />
        <SectionLegal {...legal} />
        <div className={styles.bottom_logo_container}>
          <HyamPrismicImage
            sizesByBreakpoint={{ sm: 1, lg: 1, xl: 1 }}
            field={document.data.logo}
          />
        </div>
      </div>
    </div>
  );
};

export const HyamPrismicDocumentFooterMobile = withConfigure<
  HyamPrismicDocumentFooterMobileProps,
  HyamPrismicDocumentFooterMobileStyles
>({
  componentName: "HyamPrismicDocumentFooterMobile",
  Component: _HyamPrismicDocumentFooterMobile,
  moduleStyles,
});
